export interface CustomerContact {
  name?: string | null;
  position?: string | null;
  phone?: string | null;
  email?: string | null;
  isPrimary?: boolean;
}

export interface CustomerContactFormValue {
  name: string;
  position: string;
  phone: string;
  email: string;
  isPrimary: boolean;
}

function normalizeOptionalText(value?: string | null): string | null {
  if (value === undefined || value === null) {
    return null;
  }

  const trimmed = String(value).trim();
  return trimmed ? trimmed : null;
}

function normalizeCustomerContact(contact?: CustomerContact | null): CustomerContact | null {
  if (!contact) {
    return null;
  }

  const name = normalizeOptionalText(contact.name);
  const position = normalizeOptionalText(contact.position);
  const phone = normalizeOptionalText(contact.phone);
  const email = normalizeOptionalText(contact.email);
  const isPrimary = contact.isPrimary === true;

  if (!name && !position && !phone && !email) {
    return null;
  }

  return {
    ...(name ? { name } : {}),
    ...(position ? { position } : {}),
    ...(phone ? { phone } : {}),
    ...(email ? { email } : {}),
    ...(isPrimary ? { isPrimary: true } : {})
  };
}

function enforceSinglePrimary(contacts: CustomerContact[]): CustomerContact[] {
  let primarySeen = false;
  return contacts.map((contact) => {
    if (contact.isPrimary !== true) {
      return contact;
    }
    if (primarySeen) {
      const { isPrimary, ...rest } = contact;
      return rest;
    }
    primarySeen = true;
    return contact;
  });
}

export function normalizeCustomerContacts(
  contactPeople?: CustomerContact[] | null,
  fallbackContactPerson?: string | null,
  fallbackPhone?: string | null
): CustomerContact[] {
  const normalizedContacts = Array.isArray(contactPeople)
    ? contactPeople.map(normalizeCustomerContact).filter(Boolean) as CustomerContact[]
    : [];

  if (normalizedContacts.length > 0) {
    return enforceSinglePrimary(normalizedContacts);
  }

  const fallbackContact = normalizeCustomerContact({
    name: fallbackContactPerson,
    phone: fallbackPhone
  });

  return fallbackContact ? [fallbackContact] : [];
}

export function getPrimaryCustomerContact(
  contactPeople?: CustomerContact[] | null,
  fallbackContactPerson?: string | null,
  fallbackPhone?: string | null
): CustomerContact | null {
  const normalizedContacts = normalizeCustomerContacts(
    contactPeople,
    fallbackContactPerson,
    fallbackPhone
  );

  if (normalizedContacts.length === 0) {
    return null;
  }

  return (
    normalizedContacts.find((contact) => contact.isPrimary === true) || normalizedContacts[0]
  );
}

export function getCustomerContactsSearchText(
  contactPeople?: CustomerContact[] | null,
  fallbackContactPerson?: string | null,
  fallbackPhone?: string | null
): string {
  return normalizeCustomerContacts(contactPeople, fallbackContactPerson, fallbackPhone)
    .flatMap((contact) => [contact.name, contact.position, contact.phone, contact.email])
    .filter(Boolean)
    .join(' ');
}

export function getCustomerContactsFieldValue(
  contactPeople: CustomerContact[] | null | undefined,
  field: keyof CustomerContact,
  fallbackContactPerson?: string | null,
  fallbackPhone?: string | null
): string {
  return normalizeCustomerContacts(contactPeople, fallbackContactPerson, fallbackPhone)
    .map((contact) => contact[field])
    .filter(Boolean)
    .join(' | ');
}

export function toCustomerContactFormList(
  contactPeople?: CustomerContact[] | null,
  fallbackContactPerson?: string | null,
  fallbackPhone?: string | null
): CustomerContactFormValue[] {
  const normalizedContacts = normalizeCustomerContacts(
    contactPeople,
    fallbackContactPerson,
    fallbackPhone
  );

  if (normalizedContacts.length === 0) {
    return [{ name: '', position: '', phone: '', email: '', isPrimary: false }];
  }

  return normalizedContacts.map((contact) => ({
    name: contact.name || '',
    position: contact.position || '',
    phone: contact.phone || '',
    email: contact.email || '',
    isPrimary: contact.isPrimary === true
  }));
}
